"use client"
import { Fragment } from "react"
import { TerraLink } from "../../component/TerraLink/TerraLink"
import { SpecialButton } from "../../component/SpecialButton/SpecialButton"

import { Icon, IconType } from "@/components/formatters/MXPIcon"

import {
  darkTextureWrapper,
  colorSettingDetection,
  renderHeaderConditionally,
} from "../../utils/functions"
import { useScreenSize } from "../../utils/hooks/useScreenSize"
import {
  layoutHeaderClasses,
  layoutHeaderBigClasses,
  layoutSubheaderClasses,
  layoutEyebrowClasses,
} from "@/components/tailwindHelpers"

interface WorflowStep {
  header: string
  description: string
  stepIcon: IconType
  stepType: string
  stepCTA?: SpecialButton
  image: {
    src: string
    alt: string
  }
}

export interface LayoutWorkflow {
  eyebrow?: string
  header?: string
  headerElement?: "h1" | "h2"
  subheader?: string
  description?: string
  buttons?: TerraLink[]
  presetTheme?:
    | "t2-theme-one"
    | "t2-theme-two"
    | "t2-theme-three"
    | "t2-theme-four"
    | "t2-theme-five"
    | "t2-theme-six"
    | "t2-theme-seven"
  isHeaderBig?: boolean
  workflowSteps?: WorflowStep[]
  isLCP?: boolean
}

export const LayoutWorkflow = ({
  eyebrow,
  header,
  headerElement = "h2",
  subheader,
  description,
  buttons,
  presetTheme,
  isHeaderBig,
  workflowSteps,
  isLCP = false,
}: LayoutWorkflow) => {
  const colorSetting = colorSettingDetection(presetTheme || "")
  const mainClasses = `py-10 max-sm:bg-gray-m1 ${presetTheme || ""} ${colorSetting}`
  const headerClasses = `${isHeaderBig ? layoutHeaderBigClasses : layoutHeaderClasses}
`
  const finalHeader = renderHeaderConditionally(
    headerClasses,
    header || "",
    headerElement,
  )

  const { width } = useScreenSize()

  const componentStyles = darkTextureWrapper(presetTheme || "")

  return (
    <div
      className={mainClasses}
      style={componentStyles}
      data-testid="layout-workflow"
    >
      <div className="t2-layout__content">
        <div>
          <div className="w-full px-5 lg:px-20 m-auto max-w-[1440px]">
            <div className="basis-full max-w-full px-2.5 ml-0 min-lg:basis-[58.333%] min-lg:max-w-[58.333%]">
              <div className={layoutEyebrowClasses}>{eyebrow}</div>
              {finalHeader}
              {subheader && (
                <div className={layoutSubheaderClasses}>{subheader}</div>
              )}
              {description && (
                <div
                  className="t2-layout__description"
                  dangerouslySetInnerHTML={{ __html: description || "" }}
                ></div>
              )}
            </div>
          </div>
        </div>
        <div>
          <div className="mb-[100px] max-sm:bg-gray-m1 max-sm:mb-[45px]">
            <div className="w-full px-5 lg:px-20 m-auto max-w-[1440px]">
              <div className="flex justify-center items-center flex-wrap max-sm:flex-col [&>div]:basis-0 [&>div]:grow [&>div]:p-5 [&>div]:mx-2.5">
                {workflowSteps &&
                  workflowSteps.map((step, index) => (
                    <div key={index} data-testid="workflow-step">
                      <div className="bg-blue-trmb block text-white relative text-center w-[25px] h-[25px] leading-[25px] rounded-full mb-5">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="t2-layout-workflow__step-header">
                          {step.header}
                        </h3>
                        <p className="t2-layout-workflow__step-description">
                          {step.description}
                        </p>
                        {step.stepCTA?.destination && (
                          <div className="mt-4">
                            <SpecialButton {...step.stepCTA} />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
          <div className="bg-gray-m1 pt-[45px] pb-[50px]">
            <div className="w-full px-5 lg:px-20 m-auto max-w-[1440px]">
              <div className="flex max-sm:flex-col max-sm:items-center">
                {workflowSteps &&
                  workflowSteps.map((step, index) => (
                    <Fragment key={index}>
                      <div
                        className="relative flex flex-col items-center text-center"
                        data-testid="workflow-step"
                      >
                        <div
                          className={`basis-0 text-gray-m9 text-xs font-bold leading-normal tracking-wide flex mb-10 uppercase ${
                            !step.stepIcon ? "mr-2.5" : ""
                          }`}
                        >
                          <div className="w-5 mr-2.5">
                            <Icon type={step.stepIcon} />
                          </div>
                          {step.stepType}
                        </div>
                        <div className="basis-0 grow flex flex-col justify-center [&_img]:w-full">
                          <img
                            src={step.image.src}
                            alt={step.image.alt}
                            loading={isLCP ? "eager" : "lazy"}
                          />
                        </div>
                      </div>
                      {index < workflowSteps.length - 1 && (
                        <div className="flex items-center flex-1 relative mt-[50px] max-sm:mt-0 max-sm:mb-10">
                          <div className="flex-1 block h-0.5 mr-[30px] border border-dashed border-gray-m9 max-lg:mr-2.5 max-sm:mr-0 max-sm:h-[100px]"></div>
                          <div className="w-5 h-5 absolute right-[18px] max-lg:right-0 max-lg:top-0 max-lg:bottom-0 max-lg:m-auto max-sm:-bottom-[100px] max-sm:-right-[9px]">
                            <Icon
                              type={"t2u-chevron-right"}
                              rotateDeg={width > 600 ? "0" : "90"}
                              size={20}
                              fillColor="#000"
                            />
                          </div>
                        </div>
                      )}
                    </Fragment>
                  ))}
              </div>
              <div className="hidden max-sm:flex max-sm:justify-center">
                {buttons &&
                  buttons.map((btn, index) => (
                    <TerraLink key={index} {...btn} />
                  ))}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-20 flex justify-center max-sm:hidden gap-5">
          {buttons &&
            buttons.map((btn, index) => <TerraLink key={index} {...btn} />)}
        </div>
      </div>
    </div>
  )
}
